import { apiFetch } from "@/lib/api";

import type { KittingPickLine } from "./kittingPickModel";
import type { KittingPickRunActionResponse } from "./kittingPickRunApi";

export async function kittingSubmitPick(
  line: KittingPickLine,
  quantity: number,
  options?: {
    kittingRunId?: number | null;
    inventoryBoxHexes?: string[];
    unpickedInventoryBoxHexes?: string[];
  },
): Promise<KittingPickRunActionResponse | void> {
  if (line.job.kind === "transfer_order") {
    const transferOrderId = line.job.transferOrderId;
    if (transferOrderId == null) {
      throw new Error("This transfer line has no transfer order id.");
    }
    const kittingRunId = options?.kittingRunId ?? null;
    if (kittingRunId == null) {
      throw new Error("Start a pick run to pick transfer lines. Picking is hub-only and does not pack cartons.");
    }
    return apiFetch<KittingPickRunActionResponse>("/api/manufacturing/kitting/pick-run/pick", {
      method: "POST",
      body: {
        kittingRunId,
        slotKey: line.destinationSlotId,
        transferOrderId,
        componentSku: line.sku,
        quantity,
        inventoryBoxHexes: options?.inventoryBoxHexes ?? [],
        unpickedInventoryBoxHexes: options?.unpickedInventoryBoxHexes ?? [],
      },
    });
  }

  const buildOrderId = line.job.workOrderId;
  if (buildOrderId == null) {
    throw new Error("This job line has no work order id to allocate against.");
  }

  const kittingRunId = options?.kittingRunId ?? null;
  if (kittingRunId != null) {
    return apiFetch<KittingPickRunActionResponse>("/api/manufacturing/kitting/pick-run/pick", {
      method: "POST",
      body: {
        kittingRunId,
        slotKey: line.destinationSlotId,
        buildOrderId,
        componentSku: line.sku,
        quantity,
        inventoryBoxHexes: options?.inventoryBoxHexes ?? [],
        unpickedInventoryBoxHexes: options?.unpickedInventoryBoxHexes ?? [],
      },
    });
  }

  await apiFetch(`/api/manufacturing/work-orders/${String(buildOrderId)}/allocate`, {
    method: "POST",
    body: {
      allocations: [{ sku: line.sku, quantity }],
      response: "ack",
    },
  });
}

export async function kittingReturnPick(
  line: KittingPickLine,
  quantity: number,
  options?: {
    kittingRunId?: number | null;
    inventoryBoxHex?: string | null;
    returnUnlabeledOnly?: boolean;
  },
): Promise<KittingPickRunActionResponse | void> {
  if (line.job.kind === "transfer_order") {
    const transferOrderId = line.job.transferOrderId;
    if (transferOrderId == null) {
      throw new Error("This transfer line has no transfer order id.");
    }
    const kittingRunId = options?.kittingRunId ?? null;
    if (kittingRunId == null) {
      throw new Error("Start a pick run to return transfer lines. Picking is hub-only and does not pack cartons.");
    }
    return apiFetch<KittingPickRunActionResponse>("/api/manufacturing/kitting/pick-run/return", {
      method: "POST",
      body: {
        kittingRunId,
        slotKey: line.destinationSlotId,
        transferOrderId,
        componentSku: line.sku,
        quantity,
        inventoryBoxHex: options?.inventoryBoxHex ?? null,
        returnUnlabeledOnly: options?.returnUnlabeledOnly === true,
      },
    });
  }

  const buildOrderId = line.job.workOrderId;
  if (buildOrderId == null) {
    throw new Error("This job line has no work order id to return materials against.");
  }

  const kittingRunId = options?.kittingRunId ?? null;
  if (kittingRunId != null) {
    return apiFetch<KittingPickRunActionResponse>("/api/manufacturing/kitting/pick-run/return", {
      method: "POST",
      body: {
        kittingRunId,
        slotKey: line.destinationSlotId,
        buildOrderId,
        componentSku: line.sku,
        quantity,
        inventoryBoxHex: options?.inventoryBoxHex ?? null,
        returnUnlabeledOnly: options?.returnUnlabeledOnly === true,
      },
    });
  }

  await apiFetch(`/api/manufacturing/work-orders/${String(buildOrderId)}/return-materials`, {
    method: "POST",
    body: {
      returns: [{ sku: line.sku, quantity }],
      response: "ack",
    },
  });
}
